import React, { useContext } from "react";
import { Badge } from "@glamcorner/components/lib/core/components/Badge";
import { BadgeContainer } from "@glamcorner/components/lib/core/components/BadgeContainer";
import flatten from "lodash.flatten";

import "./attribute-filters-container.scss";
import { AttributeFilter, CollectionDesignation } from "../../../common/interfaces";
import { FilterContext } from "../../FilterContext";
import { STYLE_PRICE_ATTRIBUTE_FILTER } from "../../../common/attributeFilterConstants";
import {
  AttributeFilterItem,
  AttributeFilterItemSearchableList,
  AttributeFilterItemSlider,
  AttributeFiltersComponent,
  AttributeFilterItemCheckboxList,
  AttributeFilterItemSwatch
} from "../../components";

const blackListedFilterForAllDesignation = ["size"];
const blackListedFiltersByCollectionDesignation = {
  [CollectionDesignation.Subscription]: [STYLE_PRICE_ATTRIBUTE_FILTER.code]
};

interface AttributeFiltersProps {
  attributeFilters: AttributeFilter[];
}

export const AttributeFiltersContainer: React.FC<AttributeFiltersProps> = ({ attributeFilters }) => {
  const { clearAppliedAttributeFilters, collectionFilter, appliedAttributeFilters, applyAttributeFilter } = useContext(
    FilterContext
  );

  const filteredAttributeFilters = attributeFilters.filter(f => {
    const blackListedFilterCodes = blackListedFilterForAllDesignation.concat(
      blackListedFiltersByCollectionDesignation[collectionFilter.collectionDesignation] || []
    );

    return !blackListedFilterCodes.includes(f.code);
  });

  const removeFilterHandler = (value: string) => {
    const [filterCode, filterValue] = value.split(":");
    const filter = appliedAttributeFilters.find(af => af.code === filterCode);
    if (!filter) {
      return;
    }
    const newOptions = filter.options.filter(o => o.value !== filterValue);
    const newFilter = { ...filter, options: newOptions };
    applyAttributeFilter(newFilter);
  };

  const badges = () => {
    const appliedFiltersWithOptions = appliedAttributeFilters.filter(af => af.options.length > 0);
    return flatten(
      appliedFiltersWithOptions.map(af => {
        return af.options.map(option => {
          const value = `${af.code}:${option.value}`;
          return <Badge key={value} option={{ label: option.label, value }} onClick={removeFilterHandler} />;
        });
      })
    );
  };
  const attributeFilterItems = () => {
    return filteredAttributeFilters.map((attributeFilter, index) => {
      const itemKey = `${attributeFilter.code}${index}`;
      let attributeFilterComponent;

      switch (attributeFilter.code) {
        case "#style_price":
          attributeFilterComponent = <AttributeFilterItemSlider attributeFilter={attributeFilter} />;
          break;
        case "designer":
          attributeFilterComponent = (
            <AttributeFilterItemSearchableList attributeFilter={attributeFilter} placeholder={"Find your brand"} />
          );
          break;
        case "colour_group":
          attributeFilterComponent = <AttributeFilterItemSwatch attributeFilter={attributeFilter} />;
          break;
        default:
          attributeFilterComponent = <AttributeFilterItemCheckboxList attributeFilter={attributeFilter} />;
          break;
      }
      return (
        <AttributeFilterItem attributeFilter={attributeFilter} key={itemKey}>
          {attributeFilterComponent}
        </AttributeFilterItem>
      );
    });
  };

  return (
    <div className="attribute-filter-accordion">
      <div className="attribute-filter-accordion__header">
        <h2 className="attribute-filter-accordion__header-title">Filters</h2>
        <p
          className="attribute-filter-accordion__clear-all"
          data-testid="attribute-filter-clear-all"
          onClick={clearAppliedAttributeFilters}
        >
          Clear All
        </p>
        <div className="attribute-filter-accordion__current-filters-container">
          <BadgeContainer>{badges()}</BadgeContainer>
        </div>
      </div>
      <AttributeFiltersComponent>{attributeFilterItems()}</AttributeFiltersComponent>
    </div>
  );
};
